import { HistoryOutlined } from '@ant-design/icons';
import { ISmsCreditPurchase, SmsCreditPurchaseStatus } from '@interfaces/sms-credits';
import { useApiUrl, useCustom } from '@refinedev/core';
import { convertPrice } from '@utils/resource';
import { Card, Table, Tag, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SmsCreditsSectionTitle } from './sms-credits-section-title';

const PAGE_SIZE = 10;
const STATUS_COLOR: Record<SmsCreditPurchaseStatus, string> = {
    pending: 'default',
    paid: 'processing',
    granted: 'success',
    failed: 'error',
};

type Props = { refreshKey: number };

export const SmsCreditPurchaseHistory: React.FC<Props> = ({ refreshKey }) => {
    const { t, i18n } = useTranslation(['marketplace']);
    const apiUrl = useApiUrl();
    const [pageIndex, setPageIndex] = useState(1);

    const { data, isLoading, refetch } = useCustom<{ data: ISmsCreditPurchase[]; total: number }>({
        url: `${apiUrl}/v1/sms-credits/purchases`,
        method: 'get',
        config: { query: { pageIndex, pageSize: PAGE_SIZE } },
        errorNotification: false,
    });

    // A finished purchase bumps refreshKey -> reload the list.
    useEffect(() => {
        if (refreshKey > 0) refetch();
    }, [refreshKey, refetch]);

    return (
        <Card
            title={
                <SmsCreditsSectionTitle icon={<HistoryOutlined />}>
                    {t('smsCredits.history.title')}
                </SmsCreditsSectionTitle>
            }
        >
            <Table<ISmsCreditPurchase>
                rowKey="id"
                loading={isLoading}
                dataSource={data?.data?.data ?? []}
                // Scroll only when the card is narrower than the columns (no empty scrollbar on desktop).
                scroll={{ x: 640 }}
                locale={{ emptyText: t('smsCredits.history.empty') }}
                pagination={{
                    current: pageIndex,
                    pageSize: PAGE_SIZE,
                    total: data?.data?.total ?? 0,
                    showSizeChanger: false,
                    onChange: setPageIndex,
                }}
                columns={[
                    {
                        title: t('smsCredits.history.date'),
                        dataIndex: 'createdAt',
                        render: (v: string) =>
                            new Date(v).toLocaleString(i18n.language, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            }),
                    },
                    {
                        title: t('smsCredits.history.credits'),
                        dataIndex: 'credits',
                        align: 'right',
                        render: (v: number) => v.toLocaleString(i18n.language),
                    },
                    {
                        title: t('smsCredits.history.amount'),
                        dataIndex: 'amount',
                        align: 'right',
                        render: (v: number) => convertPrice(v),
                    },
                    {
                        title: t('smsCredits.history.source'),
                        dataIndex: 'source',
                        render: (v: string) => t(`smsCredits.history.sources.${v}`),
                    },
                    {
                        title: t('smsCredits.history.status'),
                        dataIndex: 'status',
                        render: (v: SmsCreditPurchaseStatus) => (
                            // The label alone is ambiguous ("Paid" but no credits yet?): the tooltip says what happens next.
                            <Tooltip title={t(`smsCredits.history.statusHints.${v}`)}>
                                <Tag color={STATUS_COLOR[v]} tabIndex={0}>
                                    {t(`smsCredits.history.statuses.${v}`)}
                                </Tag>
                            </Tooltip>
                        ),
                    },
                ]}
            />
        </Card>
    );
};
